// src/components/onboarding/OnboardingLayout.jsx
import React from "react";
import { Logo, cn } from "../ui";
import { BRAND, RAIL_ABOUT, RAIL_PLAN, RESUME } from "./onboardingCopy";

// The web chrome the 2026-09-24 comp draws for the signed-up flow: a header
// with the brand and, on the documents pages, the three-phase rail
// ("Documents · About you · Your plan", the lit label from the server's
// `rail`); then the main column and a sticky side card holding the title,
// the side line and the page's actions. `centered` is the welcome screen's
// single column with no side card.
export default function OnboardingLayout({
  railLabel,
  sideTitle,
  sideText,
  side,
  sideBody,
  flow = false,
  resumed = false,
  centered = false,
  children,
}) {
  const bar = !centered && (sideTitle || sideText || side || sideBody);
  const lit = "tw-bg-text-accent";
  const dim = "tw-bg-divider";
  return (
    <div
      data-slot="onb-page"
      className="stars-dark tw-min-h-screen tw-bg-bg tw-font-sans tw-text-text"
    >
      <header
        data-slot="onb-header"
        className="tw-flex tw-items-center tw-justify-between tw-gap-6 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-px-8 tw-py-4 max-rail:tw-px-4"
      >
        <span
          data-slot="onb-brand"
          className="tw-flex tw-items-center tw-gap-2 tw-text-md tw-font-bold"
        >
          <Logo variant="mark" alt="" className="tw-h-6 tw-w-6" />
          {BRAND}
        </span>
        {railLabel && (
          <div
            data-slot="onb-rail"
            className="tw-flex tw-w-80 tw-max-w-full tw-flex-col tw-gap-2"
            role="group"
            aria-label={railLabel}
          >
            <div
              data-slot="onb-rail-bars"
              className="tw-flex tw-gap-2"
              aria-hidden="true"
            >
              <span
                data-on="true"
                className={`tw-h-1 tw-flex-1 tw-rounded-pill ${lit}`}
              />
              <span className={`tw-h-1 tw-flex-1 tw-rounded-pill ${dim}`} />
              <span className={`tw-h-1 tw-flex-1 tw-rounded-pill ${dim}`} />
            </div>
            <div
              data-slot="onb-rail-labels"
              className="tw-flex tw-flex-wrap tw-justify-between tw-gap-x-3 tw-text-xs tw-font-semibold tw-text-text-muted"
            >
              <span data-on="true" className="tw-text-text">
                {railLabel}
              </span>
              <span>{RAIL_ABOUT}</span>
              <span>{RAIL_PLAN}</span>
            </div>
          </div>
        )}
        <span data-slot="onb-header-end" className="tw-w-20 max-rail:tw-hidden" />
      </header>
      <main
        data-slot="onb-main"
        className="tw-mx-auto tw-max-w-3xl tw-px-4 tw-pt-8 tw-pb-8 md:tw-px-8"
      >
        {centered ? (
          <div
            data-slot="onb-centered"
            className="tw-mx-auto tw-flex tw-max-w-xl tw-flex-col tw-gap-4"
          >
            {children}
          </div>
        ) : (
          <div
            data-slot="onb-col"
            className="tw-flex tw-flex-col tw-gap-4"
          >
            {resumed && (
              <p
                data-slot="onb-notice"
                className="tw-m-0 tw-rounded-md tw-bg-accent-tint tw-px-4 tw-py-3 tw-text-sm tw-text-text"
                role="status"
              >
                {RESUME}
              </p>
            )}
            {children}
          </div>
        )}
      </main>
      {bar && (
        <>
          {!flow && !sideBody && (
            <div aria-hidden="true" className="tw-h-24 max-rail:tw-h-48" />
          )}
          <aside
            data-slot="onb-side"
            className={cn(
              "tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-bg-bar-bg tw-backdrop-blur",
              // A page whose gate measures the viewport (the agreement) or whose side
              // holds a tall block lets the bar flow after the text: a fixed bar
              // would hide the last lines the reader is counted as having seen.
              !flow && !sideBody && "tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-20",
            )}
            aria-label={sideTitle}
          >
            {sideBody && (
              <div className="tw-mx-auto tw-max-w-3xl tw-px-4 tw-pt-6 md:tw-px-8">
                {sideBody}
              </div>
            )}
            <div className="tw-mx-auto tw-flex tw-max-w-3xl tw-flex-wrap tw-items-center tw-gap-3 tw-px-4 tw-py-3 md:tw-px-8">
              {sideTitle && (
                <h2
                  data-slot="onb-side-title"
                  className="tw-m-0 tw-text-sm tw-font-bold tw-text-text"
                >
                  {sideTitle}
                </h2>
              )}
              {sideText && (
                <p className="tw-m-0 tw-text-sm tw-text-text-muted">
                  {sideText}
                </p>
              )}
              {side}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
